export interface StorageEstimateResult {
  usage: number;
  quota: number;
  available: number;
}

export interface EnsureCapacityResult {
  unknown?: true;
  available?: number;
}

/** Thrown by `ensureCapacity` when storage is known to be insufficient. */
export class QuotaError extends Error {
  constructor(bytesNeeded: number, available: number) {
    super(
      `Insufficient storage: need ${bytesNeeded} bytes but only ${available} available.`
    );
    this.name = 'QuotaError';
  }
}

/** Wraps `navigator.storage.estimate()`; returns null when unavailable. */
export const estimateStorage =
  async (): Promise<StorageEstimateResult | null> => {
    if (
      typeof navigator === 'undefined' ||
      !navigator.storage?.estimate
    ) {
      return null;
    }
    const {usage = 0, quota = 0} = await navigator.storage.estimate();
    return {usage, quota, available: Math.max(quota - usage, 0)};
  };

/**
 * Ensures at least `bytesNeeded + headroomBytes` free. Throws `QuotaError`
 * when known-insufficient; resolves `{unknown: true}` when no estimate exists.
 */
export const ensureCapacity = async (
  bytesNeeded: number,
  headroomBytes = 0
): Promise<EnsureCapacityResult> => {
  const estimate = await estimateStorage();
  if (!estimate) {
    return {unknown: true};
  }
  const required = bytesNeeded + headroomBytes;
  if (estimate.available < required) {
    throw new QuotaError(required, estimate.available);
  }
  return {available: estimate.available};
};

/** Wraps `navigator.storage.persist()`; false when unsupported or denied. */
export const requestPersistence = async (): Promise<boolean> => {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) {
    return false;
  }
  return navigator.storage.persist();
};
